import { type TypedDocumentNode, gql } from '@apollo/client';

import { CardPackCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';

import { BasicPacks } from '../BasicPacks';
import { PremiumPack } from '../PremiumPack';
import { StarPack } from '../StarPack';
import { CardPackGroupWrapper } from './CardPackGroupWrapper';
import { ScrollableHighlightedPacks } from './ScrollableHighlightedPacks';
import type { CardPackGroup_cardPackGroup } from './__generated__/index.graphql';

const splitCardPacksByType = (
  cardPacks: CardPackGroup_cardPackGroup['cardPacks']
) => {
  const result = {
    starPack: null as CardPackGroup_cardPackGroup['cardPacks'][number] | null,
    basicPacks: [] as CardPackGroup_cardPackGroup['cardPacks'][number][],
    premiumPack: null as
      | CardPackGroup_cardPackGroup['cardPacks'][number]
      | null,
  };

  cardPacks.forEach(pack => {
    if (pack.rewardedByTask) {
      result.starPack = pack;
    } else if (pack.currency === CardPackCurrency.COMMON_GEM) {
      result.premiumPack = pack;
    } else {
      result.basicPacks.push(pack);
    }
  });

  return result;
};

type Props = {
  cardPackGroup: CardPackGroup_cardPackGroup;
  onClickBuy: (cardPackGroupId: string, cardPackSlug: string) => void;
};
export const CardPackGroup = ({ cardPackGroup, onClickBuy }: Props) => {
  const { cardPacks } = cardPackGroup;
  const { premiumPack, basicPacks, starPack } = splitCardPacksByType(cardPacks);

  const isLaptopAndAbove = useIsLaptop();
  return (
    <CardPackGroupWrapper>
      {isLaptopAndAbove ? (
        premiumPack && (
          <PremiumPack
            cardPack={premiumPack}
            cardPackGroup={cardPackGroup}
            onClickBuy={onClickBuy}
          />
        )
      ) : (
        <ScrollableHighlightedPacks
          starCardPack={starPack}
          premiumCardPack={premiumPack}
          cardPackGroup={cardPackGroup}
          onClickBuy={onClickBuy}
        />
      )}
      <BasicPacks
        cardPacks={basicPacks}
        cardPackGroupId={cardPackGroup.id}
        onClickBuy={onClickBuy}
      />
      {!!starPack && !!isLaptopAndAbove && <StarPack cardPack={starPack} />}
    </CardPackGroupWrapper>
  );
};

CardPackGroup.fragments = {
  cardPackGroup: gql`
    fragment CardPackGroup_cardPackGroup on CardPackGroup {
      id
      cardPacks {
        slug
        currency
        ...PremiumPack_cardPack
        rewardedByTask {
          id
        }
        ...StarPack_cardPack
        ...ScrollableHighlightedPacks_cardPack
        ...PremiumPack_cardPack
      }
      ...PremiumPack_cardPackGroup
    }
    ${PremiumPack.fragments.cardPackGroup}
    ${PremiumPack.fragments.cardPack}
    ${StarPack.fragments.cardPack}
    ${ScrollableHighlightedPacks.fragments.cardPack}
  ` as TypedDocumentNode<CardPackGroup_cardPackGroup>,
};
